import { z } from 'zod';

// src/export/types.ts
var ExportFormat = {
  CSV: "csv",
  JSON: "json",
  NDJSON: "ndjson",
  /** SpreadsheetML 2003 (`.xls`) — opens in Excel without a heavy dependency. */
  EXCEL: "excel"
};

// src/singleton/index.ts
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}
var SCENE_DETAIL_TIERS = ["compact", "standard", "expanded"];
var MediaRefSchema = z.object({
  kind: z.string().default("placeholder"),
  value: z.string(),
  fallback: z.union([z.string(), z.null()]).default(null),
  alt: z.union([z.string(), z.null()]).default(null)
});
var SceneAssetSchema = z.object({
  token_address: z.union([z.string(), z.null()]).default(null),
  token_chain_id: z.union([z.string(), z.null()]).default(null),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  amount_raw: z.union([z.string(), z.null()]).default(null),
  amount: z.union([z.string(), z.null()]).default(null),
  usd: z.union([z.string(), z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  // 'actual' | 'quoted_minimum' — mirrors commons AmountBasis. Lets a label
  // flag a swap min-return floor as "(min)" instead of an exact amount.
  basis: z.union([z.string(), z.null()]).default(null),
  min_amount_raw: z.union([z.string(), z.null()]).default(null),
  min_amount: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var SceneSourceRefSchema = z.object({
  ref_type: z.string(),
  address: z.union([z.string(), z.null()]).default(null),
  log_index: z.union([z.number().int(), z.null()]).default(null),
  internal_tx_index: z.union([z.number().int(), z.null()]).default(null),
  path: z.array(z.string()).default([])
});
var SceneFactSchema = z.object({
  key: z.string(),
  label: z.string().default(""),
  kind: z.union([z.string(), z.null()]).default(null),
  value: z.unknown(),
  protocol_id: z.union([z.string(), z.null()]).default(null),
  source: z.union([z.string(), z.null()]).default(null),
  path_id: z.union([z.string(), z.null()]).default(null),
  confidence: z.union([z.number().min(0).max(1), z.null()]).default(null),
  icon: z.union([z.string(), z.null()]).default(null),
  description: z.union([z.string(), z.null()]).default(null)
});
var SceneNodeSchema = z.object({
  id: z.string(),
  kind: z.string().default("address"),
  label: z.string().default(""),
  sublabel: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  address: z.union([z.string(), z.null()]).default(null),
  group_id: z.union([z.string(), z.null()]).default(null),
  off_chain: z.boolean().default(false),
  entity_id: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  assets: z.array(SceneAssetSchema).default([]),
  value: z.union([z.string(), z.null()]).default(null),
  badge: z.union([z.string(), z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  confidence: z.number().min(0).max(1).default(1),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  kind: z.string().default("flow"),
  label: z.union([z.string(), z.null()]).default(null),
  asset: z.union([SceneAssetSchema, z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  from_chain: z.union([z.string(), z.null()]).default(null),
  to_chain: z.union([z.string(), z.null()]).default(null),
  cross_chain: z.boolean().default(false),
  virtual: z.boolean().default(false),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneGroupSchema = z.object({
  id: z.string(),
  group_type: z.string().default("chain"),
  chain_id: z.union([z.string(), z.null()]).default(null),
  label: z.string().default(""),
  member_node_ids: z.array(z.string()).default([]),
  color: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
z.object({
  version: z.string().default("1"),
  kind: z.string().default("semantic"),
  summary: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  // The transaction's headline "business metrics" — the SAME resolved
  // `key_attributes` groups the detail-page highlights panel shows, carried on
  // EVERY chart kind so the graph view renders a consistent highlights strip.
  // Kept as the raw snake_case wire shape (a passthrough) so the wire layer
  // stays below `storage`; a consumer normalises it with `parseKeyAttributeGroups`
  // (the same parser the detail panel + group view use) → `KeyAttributeGroup[]`.
  highlights: z.array(z.record(z.string(), z.unknown())).default([]),
  chain_ids: z.array(z.string()).default([]),
  multi_chain: z.boolean().default(false),
  layout_hint: z.string().default("hierarchical"),
  // Compact-first: mirrors the backend `GraphBuildPolicy.default_view_tier`
  // default so a scene missing the field opens on the minimal intent + highlights
  // (the user raises the tier live). The scene always carries every tier.
  default_detail_tier: z.string().default("compact"),
  attributes: z.record(z.string(), z.unknown()).default({})
});
z.object({
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  hidden_node_ids: z.array(z.string()).default([]),
  hidden_edge_ids: z.array(z.string()).default([]),
  node_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  edge_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  /**
   * Where the author put an authored node. Honoured as a pin, not a hint, and
   * only for nodes the overlay itself added — pinning a decoded node freezes a
   * layout the backend is entitled to change, and the position was never the
   * author's to give.
   */
  positions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}),
  /**
   * Who asserted this overlay, when, and why. Stamped by the service from the
   * caller's identity, never read from the element bodies — a client can no more
   * forge an author than it can forge `origin`.
   */
  authored_by: z.union([z.string(), z.null()]).default(null),
  authored_at: z.union([z.string(), z.null()]).default(null),
  authored_reason: z.union([z.string(), z.null()]).default(null),
  /**
   * The tier this overlay was published at, stamped by the read.
   *
   * On the BODY rather than left on the row, because the body is all a fold
   * receives — and without it a fold cannot tell one overlay overriding a lower
   * rung (precedence working) from two peers at the same rung contradicting each
   * other. Written by `overlay_wire` on the API and by the tx_graph store.
   */
  authored_scope: z.union([z.string(), z.null()]).default(null)
});
var GROUPING_DIMENSIONS = [
  "chain",
  "protocol",
  "entity",
  "tag",
  "label",
  "kind",
  "saga"
];
var GroupingDimensionOptionsSchema = z.object({
  /** Drop a band with fewer members than this (a band of one is just a node). */
  min_members: z.number().int().positive().optional(),
  /** Keep at most this many bands (largest first); the rest go ungrouped. */
  max_bands: z.number().int().positive().optional()
});
z.object({
  dimensions: z.array(z.enum(GROUPING_DIMENSIONS)).default([]),
  options: z.record(z.string(), GroupingDimensionOptionsSchema).default({})
});
function parseGroupingSpec(value) {
  if (!value || typeof value !== "object") return null;
  const raw = value;
  if (!Array.isArray(raw.dimensions)) return null;
  const known = new Set(GROUPING_DIMENSIONS);
  const seen = /* @__PURE__ */ new Set();
  const dimensions = [];
  for (const entry of raw.dimensions) {
    if (typeof entry !== "string" || !known.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    dimensions.push(entry);
  }
  const options = {};
  if (raw.options && typeof raw.options === "object") {
    for (const [key, opts] of Object.entries(raw.options)) {
      if (!known.has(key)) continue;
      const parsed = GroupingDimensionOptionsSchema.safeParse(opts);
      if (parsed.success) options[key] = parsed.data;
    }
  }
  return Object.keys(options).length > 0 ? { dimensions, options } : { dimensions };
}

// src/display/index.ts
var FIELD_LABELS = {
  // transaction
  canonical_tx_id: "Transaction ID",
  tx_hash: "Tx Hash",
  chain_id: "Chain",
  block_number: "Block",
  block_hash: "Block Hash",
  block_timestamp: "Time",
  transaction_index: "Index",
  type: "Type",
  finality: "Finality",
  direction: "Direction",
  status: "Status",
  total_value_raw: "Value",
  amount_hidden: "Amount hidden",
  value_usd: "Value (USD)",
  // Distinct from the `entity` catalog label below, which is the RESOLVED entity
  // (a named chip from the precedence engine). This is the raw id the row was
  // stamped with at ingest, and the two can appear side by side — a column
  // menu offering "Entity" twice is a menu that can't be used.
  primary_entity_id: "Entity ID",
  primary_protocol_id: "Primary protocol",
  protocol_ids: "Protocols",
  from: "From",
  to: "To",
  counts: "Counts",
  created_at: "Created",
  updated_at: "Updated",
  decode_confidence: "Decode confidence",
  decode_coverage: "Decode coverage",
  decode_counts: "Decode coverage detail",
  abi_source: "ABI source",
  abi_confidence: "ABI confidence",
  abi_explanation: "ABI provenance",
  // canonical-tx attr leaves (shared with `@authe/fe-commons/triggers`, which
  // resolves its field-path labels through `fieldLabel` so the rule builder and
  // the transaction table can never disagree on a name).
  from_address: "From",
  to_address: "To",
  value: "Value (native)",
  input_data: "Input Data",
  total_usd: "Value (USD)",
  // enrichment payload leaves (trigger DSL `<kind>.<path>`)
  score: "Risk Score",
  bucket: "Risk Bucket",
  policy_id: "Risk Policy",
  catalog_id: "Catalog ID",
  kind: "Kind",
  // address / catalog
  address: "Address",
  label: "Label",
  tag: "Tag",
  entity: "Entity",
  group: "Group",
  protocol: "Protocol",
  // A machine cross-chain saga (a bridge's legs tied by the protocol's own
  // correlation id) — labelled by what it shows rather than by the internal
  // "saga" term, which means nothing to an investigator.
  // What the transaction DID, as the decode read it — "Approve", "Mint
  // Signed", "Deposit Native 0.01 ETH". Distinct from `type`, which is the
  // classification ("transaction") and says nothing about the action.
  tx_action: "Action",
  // WHICH assets moved, as opposed to `value`'s how-much-in-native-and-USD.
  // An NFT purchase reads in `value` as the ETH that left it; the token it
  // bought only appears here.
  assets: "Assets",
  saga: "Cross-chain",
  // The correlation id itself, as opposed to `saga`'s "did every leg arrive".
  // Generic because the protocol names it: a CCIP message id, a Wormhole VAA.
  cross_chain_id: "Cross-chain ID",
  // Catalog attribution comes in two subjects that are easy to confuse and were:
  // what someone said about THIS TRANSFER, versus what they said about the WALLET
  // on the other end. Both label sets are called "labels" by their own API, so
  // the column headers are where the distinction has to be made visible.
  tx_labels: "Labels",
  tx_tags: "Tags",
  tx_groups: "Groups",
  tx_entity: "Entity",
  counterparty_labels: "Counterparty labels",
  counterparty_tags: "Counterparty tags",
  counterparty_groups: "Counterparty groups",
  counterparty_entity: "Counterparty entity",
  counterparty_risk: "Counterparty risk",
  row_flags: "Flags",
  // The SENDING address's attribution — the same claim as `counterparty_*` about
  // the row's other end. Named by role rather than by position for the same
  // reason `counterparty_*` is: a header reading "From labels" would be read as
  // "labels that came from somewhere".
  sender_labels: "Sender labels",
  sender_tags: "Sender tags",
  sender_groups: "Sender groups",
  sender_entity: "Sender entity",
  notes: "Note",
  triage: "Triage",
  risk_score: "Risk",
  // catalog rows (entities / tags / labels / groups — canonical commons fields)
  name: "Name",
  description: "Description",
  entity_type: "Type",
  risk_level: "Risk Level",
  website: "Website",
  twitter: "Twitter",
  category: "Category",
  color: "Colour",
  icon: "Icon",
  logo_s3_uri: "Logo",
  lifecycle: "Lifecycle",
  severity: "Severity",
  aliases: "Aliases",
  chain: "Chain",
  parent_entity_id: "Parent Entity",
  related_entity_ids: "Related Entities",
  member_tag_ids: "Tags",
  member_label_ids: "Labels",
  member_entity_ids: "Entities",
  system_default: "System Default",
  confidence: "Confidence",
  explanation: "Why",
  source: "Source",
  is_human_approved: "Approved",
  // tenancy
  org_id: "Organization",
  tenant_id: "Tenant",
  customer_id: "Customer",
  user_id: "User"
};
function titleCase(key) {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}
function fieldLabel(key) {
  return FIELD_LABELS[key] ?? titleCase(key);
}
var GRAPH_VIEW_MODES = ["hierarchical", "temporal", "force"];
GRAPH_VIEW_MODES.filter(
  (mode) => mode !== "force"
);
var GRAPH_DETAIL_TIERS = SCENE_DETAIL_TIERS;
var DEFAULT_GRAPH_DETAIL_TIER = "compact";
var GRAPH_PANEL_KEYS = ["legend", "highlights", "stats", "minimap"];
var ACTIVITY_VIEWS = ["table", "graph"];
var LIVE_MODES = ["live", "interval", "off"];
var TABLE_DENSITIES = ["comfortable", "normal", "compact"];
var DISPLAY_PREF_KEYS = {
  tableDensity: "display:table-density",
  columnVisibility: "display:columns",
  regionAliasPin: "display:region-alias-pin",
  graphViewMode: "display:graph-view-mode",
  graphDetailTier: "display:graph-detail-tier",
  graphHiddenPanels: "display:graph-panels",
  graphGrouping: "display:graph-grouping",
  activityView: "display:activity-view",
  liveMode: "display:live-mode",
  inspector: "display:inspector",
  attributionChips: "display:attribution-chips"
};
var ATTRIBUTION_KINDS = ["entity", "label", "tag", "group"];
var DEFAULT_REGION_ALIAS_PIN = "default";
function coerceEnum(value, allowed, fallback) {
  return typeof value === "string" && allowed.includes(value) ? value : fallback;
}
(class _DisplayPrefsStore {
  constructor(storage) {
    this.storage = storage;
  }
  storage;
  static {
    brandClass(this, "display/DisplayPrefsStore");
  }
  /** Derive a store for a different identity (e.g. on tenant switch). */
  with(patch) {
    return new _DisplayPrefsStore(this.storage.with(patch));
  }
  getTableDensity(fallback = "comfortable") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.tableDensity, fallback),
      TABLE_DENSITIES,
      fallback
    );
  }
  setTableDensity(density, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.tableDensity, density, level);
  }
  getGraphViewMode(fallback = "hierarchical") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.graphViewMode, fallback),
      GRAPH_VIEW_MODES,
      fallback
    );
  }
  setGraphViewMode(mode, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphViewMode, mode, level);
  }
  getGraphDetailTier(fallback = DEFAULT_GRAPH_DETAIL_TIER) {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.graphDetailTier, fallback),
      GRAPH_DETAIL_TIERS,
      fallback
    );
  }
  setGraphDetailTier(tier, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphDetailTier, tier, level);
  }
  getActivityView(fallback = "table") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.activityView, fallback),
      ACTIVITY_VIEWS,
      fallback
    );
  }
  setActivityView(view, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.activityView, view, level);
  }
  getLiveMode(fallback = "interval") {
    return coerceEnum(this.storage.get(DISPLAY_PREF_KEYS.liveMode, fallback), LIVE_MODES, fallback);
  }
  setLiveMode(mode, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.liveMode, mode, level);
  }
  /** See {@link DisplayPrefs.inspector}. Anything non-boolean reads as the default. */
  getInspector(fallback = false) {
    const value = this.storage.get(DISPLAY_PREF_KEYS.inspector, fallback);
    return typeof value === "boolean" ? value : fallback;
  }
  setInspector(enabled, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.inspector, enabled, level);
  }
  /**
   * See {@link DisplayPrefs.attributionChips}. Returns only the fields the user
   * has actually expressed, so the caller can layer it under a surface override
   * without a stored default silently outranking one.
   */
  getAttributionChips() {
    const raw = this.storage.get(DISPLAY_PREF_KEYS.attributionChips, {});
    if (!raw || typeof raw !== "object") return {};
    const stored = raw;
    const out = {};
    if (Array.isArray(stored.kinds)) {
      const kinds = stored.kinds.filter(
        (k) => ATTRIBUTION_KINDS.includes(k)
      );
      if (kinds.length > 0) out.kinds = kinds;
    }
    if (typeof stored.max === "number" && Number.isFinite(stored.max) && stored.max >= 0) {
      out.max = Math.floor(stored.max);
    }
    if (typeof stored.logos === "boolean") out.logos = stored.logos;
    if (stored.size === "sm" || stored.size === "md") out.size = stored.size;
    if (typeof stored.showProvenance === "boolean") out.showProvenance = stored.showProvenance;
    return out;
  }
  setAttributionChips(prefs, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.attributionChips, prefs, level);
  }
  getRegionAliasPin(fallback = DEFAULT_REGION_ALIAS_PIN) {
    const value = this.storage.get(DISPLAY_PREF_KEYS.regionAliasPin, fallback);
    return typeof value === "string" ? value : fallback;
  }
  setRegionAliasPin(alias, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.regionAliasPin, alias, level);
  }
  /**
   * Overlay panels the user dismissed on one graph surface.
   *
   * Keyed per graph (as column visibility is keyed per table) because dismissing
   * the legend on a dense case graph shouldn't hide it on a two-node transaction
   * graph. Stores the DISMISSED set, not the visible one, so a panel added later
   * shows up by default instead of being retroactively hidden by an older pref.
   * Unknown keys are dropped on read.
   */
  getGraphHiddenPanels(graphId, fallback = []) {
    const stored = this.storage.get(
      `${DISPLAY_PREF_KEYS.graphHiddenPanels}:${graphId}`,
      fallback
    );
    if (!Array.isArray(stored)) return fallback;
    return stored.filter(
      (key) => GRAPH_PANEL_KEYS.includes(key)
    );
  }
  setGraphHiddenPanels(graphId, hidden, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.graphHiddenPanels}:${graphId}`, [...hidden], level);
  }
  /**
   * How the user chose to band one graph surface (the "Group by" order).
   *
   * Keyed per graph for the same reason the panels are: grouping a case file by
   * entity says nothing about how a single transaction should be drawn. Parsed
   * on read, so a dimension this build no longer knows is dropped rather than
   * breaking the graph.
   */
  getGraphGrouping(graphId, fallback = null) {
    const stored = this.storage.get(
      `${DISPLAY_PREF_KEYS.graphGrouping}:${graphId}`,
      void 0
    );
    return parseGroupingSpec(stored) ?? fallback;
  }
  setGraphGrouping(graphId, grouping, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.graphGrouping}:${graphId}`, grouping, level);
  }
  /** Column visibility for one table id (`{ [columnId]: visible }`). */
  getColumnVisibility(tableId, fallback = {}) {
    return this.storage.get(
      `${DISPLAY_PREF_KEYS.columnVisibility}:${tableId}`,
      fallback
    );
  }
  setColumnVisibility(tableId, visibility, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.columnVisibility}:${tableId}`, visibility, level);
  }
  /** Load the non-table-scoped prefs as one bag (column visibility is per-table). */
  load() {
    return {
      tableDensity: this.getTableDensity(),
      regionAliasPin: this.getRegionAliasPin(),
      graphViewMode: this.getGraphViewMode(),
      graphDetailTier: this.getGraphDetailTier(),
      activityView: this.getActivityView(),
      liveMode: this.getLiveMode(),
      inspector: this.getInspector(),
      attributionChips: this.getAttributionChips()
    };
  }
  /**
   * Persist whichever prefs are present (skips `undefined` fields).
   *
   * `true` only when every field it actually wrote persisted — same contract as
   * the individual setters, so a bulk save cannot claim more than they do.
   * Every field is attempted regardless: a full store is a property of the
   * store, not of one key, but stopping early would make the result depend on
   * field order.
   */
  save(prefs, level) {
    let stored = true;
    if (prefs.tableDensity) stored = this.setTableDensity(prefs.tableDensity, level) && stored;
    if (prefs.regionAliasPin != null) {
      stored = this.setRegionAliasPin(prefs.regionAliasPin, level) && stored;
    }
    if (prefs.graphViewMode) stored = this.setGraphViewMode(prefs.graphViewMode, level) && stored;
    if (prefs.graphDetailTier) {
      stored = this.setGraphDetailTier(prefs.graphDetailTier, level) && stored;
    }
    if (prefs.activityView) stored = this.setActivityView(prefs.activityView, level) && stored;
    if (prefs.liveMode) stored = this.setLiveMode(prefs.liveMode, level) && stored;
    if (prefs.inspector != null) stored = this.setInspector(prefs.inspector, level) && stored;
    if (prefs.attributionChips) {
      stored = this.setAttributionChips(prefs.attributionChips, level) && stored;
    }
    if (prefs.columnVisibility) {
      for (const [tableId, visibility] of Object.entries(prefs.columnVisibility)) {
        stored = this.setColumnVisibility(tableId, visibility, level) && stored;
      }
    }
    if (prefs.graphHiddenPanels) {
      for (const [graphId, hidden] of Object.entries(prefs.graphHiddenPanels)) {
        stored = this.setGraphHiddenPanels(graphId, hidden, level) && stored;
      }
    }
    if (prefs.graphGrouping) {
      for (const [graphId, grouping] of Object.entries(prefs.graphGrouping)) {
        stored = this.setGraphGrouping(graphId, grouping, level) && stored;
      }
    }
    return stored;
  }
});

// src/export/cells.ts
function formatCell(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "bigint" || typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
function resolveColumns(columns) {
  return columns.map(
    (c) => typeof c === "string" ? { key: c, header: fieldLabel(c) } : { ...c, header: c.header ?? fieldLabel(c.key) }
  );
}
function cellValue(row, col) {
  const raw = col.value ? col.value(row) : row[col.key];
  return col.format ? col.format(raw, row) : formatCell(raw);
}
function rawCellValue(row, col) {
  return col.value ? col.value(row) : row[col.key];
}
var FORMULA_TRIGGER = /^[=+\-@\t\r]/;
var PLAIN_NUMBER = /^[-+]?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][-+]?\d+)?$/;
function neutralizeSpreadsheetFormula(field) {
  if (!FORMULA_TRIGGER.test(field)) return field;
  if (PLAIN_NUMBER.test(field)) return field;
  return `'${field}`;
}
function escapeCsvField(field, delimiter, sanitize = true) {
  const safe = sanitize ? neutralizeSpreadsheetFormula(field) : field;
  if (safe.includes('"') || safe.includes(delimiter) || safe.includes("\n") || safe.includes("\r")) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}
function escapeXml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}

// src/export/iter.ts
async function* toAsync(source) {
  if (typeof source[Symbol.asyncIterator] === "function") {
    yield* source;
  } else {
    for (const item of source) yield item;
  }
}
async function* chunk(source, size) {
  const max = Math.max(1, size);
  let batch = [];
  for await (const item of toAsync(source)) {
    batch.push(item);
    if (batch.length >= max) {
      yield batch;
      batch = [];
    }
  }
  if (batch.length > 0) yield batch;
}

// src/export/csv.ts
var CSV_BOM = "\uFEFF";
var DEFAULT_CSV_DELIMITER = ",";
async function* streamCsv(source, columns, opts = {}) {
  const cols = resolveColumns(columns);
  const delimiter = opts.delimiter ?? DEFAULT_CSV_DELIMITER;
  const newline = opts.newline ?? "\r\n";
  const batchRows = Math.max(1, opts.batchRows ?? 500);
  const sanitize = opts.sanitizeFormulas !== false;
  if (opts.bom) yield CSV_BOM;
  if (opts.includeHeader !== false) {
    yield cols.map((c) => escapeCsvField(c.header, delimiter, sanitize)).join(delimiter) + newline;
  }
  let buffer = [];
  for await (const row of toAsync(source)) {
    buffer.push(cols.map((c) => escapeCsvField(cellValue(row, c), delimiter, sanitize)).join(delimiter));
    if (buffer.length >= batchRows) {
      yield buffer.join(newline) + newline;
      buffer = [];
    }
  }
  if (buffer.length > 0) yield buffer.join(newline) + newline;
}
async function toCsv(rows, columns, opts = {}) {
  let out = "";
  for await (const chunk2 of streamCsv(rows, columns, opts)) out += chunk2;
  return out;
}

// src/export/json.ts
function projectRow(row, columns) {
  const out = {};
  for (const col of columns) out[col.key] = rawCellValue(row, col);
  return out;
}
async function* streamNdjson(source, options = {}) {
  const cols = options.columns ? resolveColumns(options.columns) : null;
  for await (const row of toAsync(source)) {
    const obj = cols ? projectRow(row, cols) : row;
    yield `${JSON.stringify(obj)}
`;
  }
}
async function* streamJsonArray(source, options = {}) {
  const cols = options.columns ? resolveColumns(options.columns) : null;
  yield "[";
  let first = true;
  for await (const row of toAsync(source)) {
    const obj = cols ? projectRow(row, cols) : row;
    yield `${first ? "" : ","}${JSON.stringify(obj)}`;
    first = false;
  }
  yield "]";
}
async function toJson(rows, options = {}) {
  const cols = options.columns ? resolveColumns(options.columns) : null;
  const out = [];
  for await (const row of toAsync(rows)) out.push(cols ? projectRow(row, cols) : row);
  return JSON.stringify(out, null, options.indent ?? 0);
}

// src/export/excel.ts
function cellXml(text, type) {
  return `<Cell><Data ss:Type="${type}">${escapeXml(text)}</Data></Cell>`;
}
function toExcelXml(rows, columns, opts = {}) {
  const cols = resolveColumns(columns);
  const sheet = escapeXml(opts.sheetName ?? "Export");
  const headerRow = `<Row>${cols.map((c) => cellXml(c.header, "String")).join("")}</Row>`;
  const bodyRows = [];
  for (const row of rows) {
    const cells = cols.map((c) => {
      const raw = rawCellValue(row, c);
      const isNumber = typeof raw === "number" && Number.isFinite(raw);
      return isNumber ? cellXml(String(raw), "Number") : cellXml(cellValue(row, c), "String");
    });
    bodyRows.push(`<Row>${cells.join("")}</Row>`);
  }
  return [
    '<?xml version="1.0"?>',
    '<?mso-application progid="Excel.Sheet"?>',
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">',
    `<Worksheet ss:Name="${sheet}"><Table>`,
    headerRow,
    bodyRows.join(""),
    "</Table></Worksheet></Workbook>"
  ].join("");
}

// src/export/blob.ts
var EXPORT_MIME = {
  csv: "text/csv;charset=utf-8",
  json: "application/json",
  ndjson: "application/x-ndjson",
  excel: "application/vnd.ms-excel",
  pdf: "application/pdf"
};
var EXPORT_EXTENSION = {
  csv: "csv",
  json: "json",
  ndjson: "ndjson",
  excel: "xls",
  pdf: "pdf"
};
function slugify(base) {
  return base.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "export";
}
function stamp(date) {
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}-${p(date.getHours())}${p(date.getMinutes())}`;
}
function buildExportFilename(base, format, opts = {}) {
  const ext = EXPORT_EXTENSION[format];
  const slug = slugify(base);
  if (!opts.timestamp) return `${slug}.${ext}`;
  const date = opts.timestamp instanceof Date ? opts.timestamp : /* @__PURE__ */ new Date();
  return `${slug}-${stamp(date)}.${ext}`;
}
async function streamToBlob(chunks, type) {
  const parts = [];
  for await (const c of toAsync(chunks)) parts.push(c);
  return new Blob(parts, { type });
}
function triggerBrowserDownload(data, filename, type) {
  if (typeof document === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    return false;
  }
  const blob = typeof data === "string" ? new Blob([data], { type: type ?? "text/plain;charset=utf-8" }) : data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1e4);
  return true;
}

export { CSV_BOM, DEFAULT_CSV_DELIMITER, EXPORT_EXTENSION, EXPORT_MIME, ExportFormat, buildExportFilename, cellValue, chunk, escapeCsvField, escapeXml, formatCell, neutralizeSpreadsheetFormula, rawCellValue, resolveColumns, streamCsv, streamJsonArray, streamNdjson, streamToBlob, toAsync, toCsv, toExcelXml, toJson, triggerBrowserDownload };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map